import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'

export interface PermissionSocketEvent {
  type: 'permissions.updated'
  timestamp: string
  scope: 'menu-permissions' | 'employee-permissions'
  menuIds?: string[]
  roleIds?: number[]
  employeeId?: string
  updatedBy?: string
}

const socket = ref<WebSocket | null>(null)
const reconnectTimer = ref<number | null>(null)
const isConnecting = ref(false)

const getWsUrl = () => {
  const graphqlUrl = (import.meta.env.VITE_GRAPHQL_URL as string) || 'http://localhost:4000/graphql'
  const endpoint = new URL(graphqlUrl)
  endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
  endpoint.pathname = '/ws/permissions'
  endpoint.search = ''
  return endpoint.toString()
}

const clearReconnectTimer = () => {
  if (reconnectTimer.value !== null) {
    window.clearTimeout(reconnectTimer.value)
    reconnectTimer.value = null
  }
}

const scheduleReconnect = () => {
  clearReconnectTimer()
  reconnectTimer.value = window.setTimeout(() => {
    connectPermissionsSocket()
  }, 3000)
}

const handlePermissionEvent = async (event: PermissionSocketEvent) => {
  if (event.type !== 'permissions.updated') return

  const authStore = useAuthStore()
  const permissionsStore = usePermissionsStore()

  if (!authStore.user) return

  const currentRoleIds = authStore.user.roles.map((role) => role.id)
  const isRelevantMenuUpdate = event.scope === 'menu-permissions' && (!event.roleIds?.length || event.roleIds.some((roleId) => currentRoleIds.includes(roleId)))
  const isRelevantEmployeeUpdate = event.scope === 'employee-permissions' && event.employeeId === authStore.user.id

  if (!isRelevantMenuUpdate && !isRelevantEmployeeUpdate) {
    return
  }

  await permissionsStore.fetchMenuItems(true)

  if (isRelevantEmployeeUpdate) {
    await authStore.fetchProfile()
  }
}

export const connectPermissionsSocket = () => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated || isConnecting.value || socket.value?.readyState === WebSocket.OPEN) {
    return
  }

  clearReconnectTimer()
  isConnecting.value = true

  try {
    const ws = new WebSocket(getWsUrl())

    ws.addEventListener('open', () => {
      socket.value = ws
      isConnecting.value = false
      console.info('🔌 Permissions WebSocket connected')
    })

    ws.addEventListener('message', async (message) => {
      try {
        const payload = JSON.parse(message.data) as PermissionSocketEvent
        await handlePermissionEvent(payload)
      } catch (error) {
        console.error('Failed to process permissions websocket message:', error)
      }
    })

    ws.addEventListener('close', () => {
      if (socket.value === ws) {
        socket.value = null
      }
      isConnecting.value = false
      if (useAuthStore().isAuthenticated) {
        scheduleReconnect()
      }
    })

    ws.addEventListener('error', (error) => {
      console.error('Permissions WebSocket error:', error)
      isConnecting.value = false
      ws.close()
    })
  } catch (error) {
    console.error('Failed to initialize permissions websocket:', error)
    isConnecting.value = false
    scheduleReconnect()
  }
}

export const disconnectPermissionsSocket = () => {
  clearReconnectTimer()
  isConnecting.value = false
  if (socket.value) {
    socket.value.close()
    socket.value = null
  }
}
